import { useCallback } from 'react';

import {
  mergeStructureInstructionsIntoData,
  removeInstructionsFromData,
} from 'src/components/RichSlate';
import { SLATE_TEXT_FORMATS } from 'src/lib/slate';
import { useBrandVoicesActions } from 'src/store';

import { usePrepareStructureInstructionsDefaults } from './usePrepareStructureInstructionsDefaults';
import { preparePromptFieldStructureInstructions } from './usePromptFieldStructureInstructions';

export const useActualizeStructureInstructionsValue = () => {
  const prepareStructureInstructionsDefaults = usePrepareStructureInstructionsDefaults();
  const brandVoicesActions = useBrandVoicesActions();

  return useCallback(
    ({ generationTool, language, instructionTypes, currentValue, mergeDefaults }) => {
      const brandVoicesWithTerms = brandVoicesActions.getVoicesWithTerms({ language });
      const customFormulas = brandVoicesActions.getDefaultVoiceCustomFormulas({ language });
      const customFormats = brandVoicesActions.getDefaultVoiceCustomFormats({ language });

      const availableInstructions = preparePromptFieldStructureInstructions({
        generationTool,
        brandVoicesWithTerms,
        customFormulas,
        customFormats,
        instructionTypes,
      });

      // take "placeholders" from the current "structure" value
      const placeholdersValues = currentValue?.placeholders
        ? Object.values(currentValue.placeholders)
        : [];

      // filter out all still available "instruction" placeholders
      const unavailablePlaceholders = placeholdersValues.filter((placeholder) => {
        // take only "instruction" placeholders
        if (placeholder.type !== SLATE_TEXT_FORMATS.PROMPT_INSTRUCTION) {
          return false;
        }

        // take only "instructions" that are not available in the current session
        return (
          availableInstructions.find(
            (item) => placeholder.instructionType === item.type && item.id === placeholder.id
          ) == null
        );
      });
      const instructionsToRemove = unavailablePlaceholders.map((placeholder) => ({
        type: placeholder.instructionType,
        id: placeholder.id,
      }));

      // remove all un-available instructions from the current value
      const actualizedValue = removeInstructionsFromData(currentValue, instructionsToRemove);

      // end here, if we don't need to merge "defaults"
      if (!mergeDefaults) {
        return actualizedValue;
      }

      // prepare "default" structure instructions
      const defaults = prepareStructureInstructionsDefaults({
        generationTool,
        language,
        instructionTypes,
      });

      // merge with possibly existing "default" structure instructions
      return mergeStructureInstructionsIntoData(actualizedValue, defaults.initialInstructions);
    },
    [brandVoicesActions, prepareStructureInstructionsDefaults]
  );
};
